// Packages
import React, { useState, useEffect, useReducer, useMemo } from "react";
import { connect } from "react-redux";
import { FormattedMessage } from 'react-intl';
// Components
import CardSectionWrapper from "../cardSectionWrapper";
import CcAvenueForm from "../ccAvenueForm";
import FiservForm from "../fiservForm";
import QpayForm from "../qpayForm";
import QpayCardSectionWrapper from "../qpayCardSectionWrapper";
import IPay88Form from "../iPay88Form";
import InlineLoader from "../../components/inlineLoader";
import OmiseCardSectionWrapper from "../../components/omiseCardSectionWrapper";
import AdyenSectionWrapper from "../../components/adyenSectionWrapper";
import StripeConnectWrapper from "../../mobileComponents/paymentTypes/stripeConnect/wrapper";
import MastercardDirectPaymentWrapper from "../../mobileComponents/paymentTypes/mastercardDirectPayment/wrapper";
import MasterCardSectionWrapper from "../../components/masterCardSectionWrapper";
import PaymentTypeConditionalWrapper from "../../components/paymentTypeConditionalWrapper";
// Icons, Images etc.
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMoneyBillWave, faCreditCard } from "@fortawesome/free-solid-svg-icons";
// Redux Operations
import { loyaltyOperations } from "../../state/features/loyalty";
import { paymentTypeOperations } from "../../state/features/paymentTypes";
import TipsWidgetMobile from "../tipsWidgetMobile";
import { calculateAmountsAndBreakdowns, formatDecimal } from "../../helpers/itemCalculations";
import { cartOperations } from "../../state/features/cart";
import { riderTipOperations } from "../../state/features/riderTip";
// Helpers, Utils etc.
import { MASTERCARD_PAYMENT_TYPE } from "../../helpers/constants";
import GenericPaymentTypeWrapper from "../../components/genericPaymentTypeWrapper";
import { usePromotionsContext } from "../../hooks/usePromotionsContext";
import { combineCarts } from "../../features/promotions/helpers/utils";
import GenericStandalonePaymentTypeWrapper from "../../components/genericStandalonePaymentTypeWrapper";
import CheckoutBreakdownsMobile from "../checkoutBreakdowns";

interface IPaymentDetailsMobileProps {
  paymentTypes: any;
  payload:any;
  user: any;
  userEmail: string;
  userName: string;
  loyalty: any;
  orderType: string;
  loyaltyEnabled: boolean;
  cardToken: any;
  handleStripeCardSelection: any;
  showPointsEarnedPopup: any;
  onPaymentSelect: any;
  updateUserEmail: any;
  fetchLoyalty: any;
  myFatoorahSelectedPaymentMode: any;
  updateMyFatoorahSelectedPaymentMode: any;
  fetchMyFatoorahPaymentModes: any;
  togglePlaceOrderButton: any;
  updateCardToken: any;
  companyDetails: any;
  setTipAmount: any;
  cart: any;
  menu: any;
  currency: any;
  discount: any;
  tipsEnabled: boolean;
  selectedArea: any;
  outlet: any;
  riderTip: number;
  removeRiderTip: () => void;
  setGenericPaymentTypeSelected: any;
  setGenericStandalonePaymentTypeSelected: any;
  userDetailsForGenericPayments: any;
  paymentDetailsForGenericPayments: any;
  buildGenericPaymentsPayload: any;
  buildGenericStandalonePaymentsPayload: any;
}

function PaymentDetailsMobile(props: IPaymentDetailsMobileProps) {
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);
  const [myFatoorahPaymentModes, setMyFatoorahPaymentModes] = useState(
    [] as any
  );
  const [myFatoorahLoading, setMyFatoorahLoading] = useState(false);
  const [myFatoorahError, setMyFatoorahError] = useState<string | null>(null);
  const [showTips, setShowTips] = useState(true);
  const { appliedPromotionsData, freeItemCart } = usePromotionsContext();

  const initialState: any = {
    amountBreakdowns: {
      subTotal: 0,
      total: 0,
      taxTotal: 0,
      discountTotal: 0,
      loyaltyTotal: 0,
      tip: 0,
      surchargeBreakDown: [],
    },
  };
  const [state, dispatch] = useReducer((state: any, action: any) => {
    switch (action.type) {
      case "setAmountBreakdowns":
        return { ...state, amountBreakdowns: action.data };
      default:
        return state;
    }
  }, initialState);

  useEffect(() => {
    if (
      props.user.id &&
      props.loyaltyEnabled &&
      Object.keys(props.loyalty).length === 0
    ) {
      props.fetchLoyalty();
    }
  }, [props.user.id]);

  useEffect(() => {
    if (!selectedPaymentType && props.paymentTypes.length > 0) {
      setSelectedPaymentType(props.paymentTypes[0].payment_type);
      handlePaymentType(props.paymentTypes[0]);
    }
  }, [props.paymentTypes]);

  useEffect(() => {
    updateAmountBreakdowns();
  }, [props.cart]);

  useEffect(() => {
    updateAmountBreakdowns();
  }, [props.riderTip]);

  useEffect(() => {
    if (
      props.tipsEnabled &&
      !["cash_on_delivery", "card_on_delivery"].includes(selectedPaymentType) &&
      props.orderType === "delivery"
    ) {
      setShowTips(true);
    } else {
      setShowTips(false);
      props.removeRiderTip();
    }
  }, [props.tipsEnabled, selectedPaymentType, props.orderType]);

  const appliedOrderPromotionName = useMemo(() => {
    let promotionName = "";
    if (appliedPromotionsData.orderPromotion.length > 0) {
      promotionName = appliedPromotionsData.orderPromotion[0].name;
    }
    return promotionName;
  }, [appliedPromotionsData.orderPromotion]);

  const handlePaymentType = (paymentType: any) => {
    setSelectedPaymentType(paymentType.payment_type);
    props.onPaymentSelect(paymentType.payment_type, paymentType.title);

    if (paymentType.generic_integration) {
      props.setGenericPaymentTypeSelected(true);
    } else {
      props.setGenericPaymentTypeSelected(false);
    }

    if (paymentType.generic_standalone_integration) {
      props.setGenericStandalonePaymentTypeSelected(true);
    } else {
      props.setGenericStandalonePaymentTypeSelected(false);
    }

    if (paymentType.payment_type == "my_fatoorah" && props.myFatoorahSelectedPaymentMode == "") {
      handleMyFatoorahPaymentModes(paymentType);
    }
  };

  const handleMyFatoorahPaymentModes = (paymentType: any) => {
    setMyFatoorahLoading(true);
    setMyFatoorahError(null);
    props.fetchMyFatoorahPaymentModes(props.cart.total).then((response: any) => {
      if (!response.error) {
        setMyFatoorahPaymentModes(response.payload.data);
        setMyFatoorahLoading(false);
        setMyFatoorahError(null);
        props.updateMyFatoorahSelectedPaymentMode(response.payload.data[0]?.id);
      } else {
        setMyFatoorahLoading(false);
        setMyFatoorahError(response.error?.response?.data?.error || "Something went wrong. Please try again later.");
      }
    })
  };

  const emailBoxShouldShow = (paymentType: any, selectedPaymentType: any, email: any) => {
    if (!email) {
      return (
        (paymentType.payment_type == "pay_u" &&  selectedPaymentType == "pay_u") ||
        (paymentType.payment_type == "qpay" &&  selectedPaymentType == "qpay")
      );
    }
    return false;
  };

  const updateAmountBreakdowns = () => {
    const currentUserCart = combineCarts(
      appliedPromotionsData.promotionAppliedCart,
      freeItemCart
    );
    let amountBreakdowns: any = calculateAmountsAndBreakdowns(
      currentUserCart,
      props.menu,
      props.orderType,
      props.discount,
      props.loyalty.redeemedPoints,
      props.selectedArea,
      props.outlet.delivery_charge_id,
      props.riderTip,
      appliedPromotionsData.orderPromotion ?? []
    ) as any;
    dispatch({ type: "setAmountBreakdowns", data: amountBreakdowns });
  };

  const removeTipFromBreakdown = () => {
    props.removeRiderTip();
  };

  return (
    <div className="payment-details">
      <label className="input-label bold mb-0">
        <FormattedMessage
          id="checkout.payment_options"
          defaultMessage="Payment Options"
        />
      </label>
      <div className="payment-methods">
        <div>
          {props.paymentTypes.map((paymentType: any) => {
            return (
              <div
                key={paymentType.id}
                className={
                  selectedPaymentType === paymentType.payment_type
                    ? "form-check method selected"
                    : "form-check method"
                }
                onClick={() => handlePaymentType(paymentType)}
              >
                <label
                  className="form-check-label"
                  htmlFor={paymentType.payment_type}
                >
                  <input
                    className="form-check-input"
                    type="radio"
                    id={paymentType.payment_type}
                    checked={selectedPaymentType === paymentType.payment_type}
                    readOnly
                  />
                  <FontAwesomeIcon
                    icon={
                      paymentType.payment_type == "cash_on_delivery"
                        ? faMoneyBillWave
                        : faCreditCard
                      }
                    size="sm"
                  />
                  <span>{paymentType.title}</span>
                </label>

                <PaymentTypeConditionalWrapper
                  paymentType={paymentType}
                  selectedPaymentType={selectedPaymentType}
                  standaloneChildren={
                    paymentType.generic_standalone_integration && (
                      <GenericStandalonePaymentTypeWrapper
                        orderPayload={props.payload}
                        togglePlaceOrderButton={(status: boolean) =>
                          props.togglePlaceOrderButton(status)
                        }
                        buildGenericStandalonePaymentsPayload={
                          props.buildGenericStandalonePaymentsPayload
                        }
                        userDetails={props.userDetailsForGenericPayments()}
                        paymentDetails={props.paymentDetailsForGenericPayments()}
                      />
                    )
                  }
                >
                  {/* Legacy Components - No boolean checks needed! */}
                  {paymentType.payment_type == "my_fatoorah" &&
                  selectedPaymentType == "my_fatoorah" && (
                    <div className="payment-subsection">
                      {myFatoorahLoading ? (
                        <div className="loader-container">
                          <InlineLoader />
                        </div>
                      ) : myFatoorahError ? (
                        <small className="text-danger d-block mt-2">{myFatoorahError}</small>
                      ) : (
                      <div className="selections">
                        {myFatoorahPaymentModes.length > 1 ? (
                          <small>
                            <FormattedMessage
                              id="checkout.choose_payment_mode"
                              defaultMessage="Choose your payment mode"
                            />
                          </small>
                        ) : null}

                        {myFatoorahPaymentModes.map((myFatoorahPaymentMode: any) => {
                          return(
                            <ul key={myFatoorahPaymentMode.id} >
                              <li>
                                <label>
                                  <input
                                    type="radio"
                                    name="myFatoorahSelectedPaymentMode"
                                    onClick={() =>
                                      props.updateMyFatoorahSelectedPaymentMode(myFatoorahPaymentMode.id)
                                    }
                                    checked={
                                      props.myFatoorahSelectedPaymentMode ==
                                      myFatoorahPaymentMode.id
                                    }
                                  />
                                  {myFatoorahPaymentMode.title}
                                </label>
                              </li>
                            </ul>
                          )
                        })}
                      </div>
                      )}
                    </div>
                    )}

                  {paymentType.payment_type == "stripe" &&
                    selectedPaymentType == "stripe" && (
                      <CardSectionWrapper
                        stripeKey={paymentType.public_key}
                        payload={props.payload}
                        onCardSelect={(cardData: any) =>
                          props.handleStripeCardSelection(cardData)
                        }
                        showPointsEarnedPopup={(earnedLoyalty: any) =>
                          props.showPointsEarnedPopup(earnedLoyalty)
                        }
                        togglePlaceOrderButton={(status: boolean) =>
                          props.togglePlaceOrderButton(status)
                        }
                      />
                    )}

                  {paymentType.payment_type == "stripe_connect" &&
                    selectedPaymentType == "stripe_connect" && (
                    <StripeConnectWrapper
                      orderPayload={props.payload}
                      togglePlaceOrderButton={(status: boolean) =>
                        props.togglePlaceOrderButton(status)
                      }
                    />
                  )}

                  {paymentType.payment_type == "qpay" &&
                  selectedPaymentType == "qpay" &&
                  paymentType.can_save_cards && (
                    <QpayCardSectionWrapper
                      updateCardToken={(token: any) =>
                        props.updateCardToken(token)
                      }
                    />
                  )}

                  {paymentType.payment_type == "omise" &&
                    selectedPaymentType == "omise" && (
                      <OmiseCardSectionWrapper
                        omiseKey={ paymentType.public_key }
                        payload={ props.payload }
                        currency={ props.companyDetails.currency }
                        showPointsEarnedPopup={(earnedLoyalty: any) =>
                          props.showPointsEarnedPopup(earnedLoyalty)
                        }
                        togglePlaceOrderButton={(status: boolean) =>
                          props.togglePlaceOrderButton(status)
                        }
                        companyDecimalPrecision={ props.companyDetails.decimal_precision }
                      />
                  )}

                  {paymentType.payment_type == "adyen" &&
                      selectedPaymentType == "adyen" && (
                    <div className="payment-subsection adyen">
                      <div className="selections">
                        <AdyenSectionWrapper
                          payload={props.payload}
                          paymentType={paymentType}
                          showPointsEarnedPopup={(earnedLoyalty: any) =>
                            props.showPointsEarnedPopup(earnedLoyalty)
                          }
                          togglePlaceOrderButton={(status: boolean) =>
                            props.togglePlaceOrderButton(status)
                          }
                        />
                      </div>
                    </div>
                  )}

                    {paymentType.payment_type == MASTERCARD_PAYMENT_TYPE &&
                      selectedPaymentType == MASTERCARD_PAYMENT_TYPE && (
                        paymentType.direct_payment_enabled ?
                          <MastercardDirectPaymentWrapper
                            payload={props.payload}
                            togglePlaceOrderButton={(status: boolean) =>
                              props.togglePlaceOrderButton(status)
                            }
                            googlePayEnabled={paymentType?.google_pay_enabled}
                            applePayEnabled={paymentType?.apple_pay_enabled}
                          />
                        : 
                          <MasterCardSectionWrapper
                            orderPayload={props.payload}
                            togglePlaceOrderButton={(status: boolean) =>
                              props.togglePlaceOrderButton(status)
                            }
                          />
                        )}

                  {paymentType.generic_integration && (
                    <GenericPaymentTypeWrapper
                      orderPayload={props.payload}
                      togglePlaceOrderButton={(status: boolean) =>
                        props.togglePlaceOrderButton(status)
                      }
                      buildGenericPaymentsPayload={
                        props.buildGenericPaymentsPayload
                      }
                      userDetails={props.userDetailsForGenericPayments()}
                      paymentDetails={props.paymentDetailsForGenericPayments()}
                    />
                  )}

                  {emailBoxShouldShow(paymentType,
                  selectedPaymentType, props.user.email) ? (
                    <div className="customer-details-section-mb mt-2">
                      <div className="add-customer-details-mb mt-2">
                        <div className="heading">
                        </div>
                        <div className="form-group add-customer-form-mb">
                          <React.Fragment>
                            <label className="input-label bold mb-2">
                              <FormattedMessage
                                id="global.email"
                                defaultMessage="Email"
                              />
                            </label>
                            <input
                              className="form-control custom-input mb-2"
                              type="text"
                              value={props.userEmail}
                              onChange={(event: any) => {
                                props.updateUserEmail(event.target.value)
                              }}
                              id="user_email"
                              required
                            />
                          </React.Fragment>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </PaymentTypeConditionalWrapper>
              </div>
            );
          })}
        </div>
        {selectedPaymentType == "cc_avenue" && (
          <CcAvenueForm
            CCAvenueFormData={props.payload}
          />
        )}
        {selectedPaymentType == "qpay" && (
          <QpayForm
            qpayFormData={props.payload}
            email={props.userEmail}
            name={props.userName}
            cardToken={props.cardToken}
          />
        )}
        {selectedPaymentType == "i_pay88" && (
          <IPay88Form
            IPay88FormData={props.payload}
          />
        )}
        {selectedPaymentType == "fiserv" && (
          <FiservForm
            fiservFormData={props.payload}
          />
        )}
      </div>

      {showTips && <TipsWidgetMobile />}
      <CheckoutBreakdownsMobile
        showTips={showTips}
        amountBreakdowns={state.amountBreakdowns}
        appliedOrderPromotionName={appliedOrderPromotionName}
        removeTipFromBreakdown={removeTipFromBreakdown}
      />
    </div>
  );
}

const mapStateToProps = (state: any) => {
  let user = state.user;
  let loyalty = state.loyalty;
  let orderType = state.session.order_type;
  let loyaltyEnabled = state.loyaltyConfig[orderType]?.enabled;
  let cart = state.cart;
  let menu = state.menu;
  let currency = state.company.currency;
  let discount = state.discount;
  let tipsEnabled = state.company.tip_settings?.has_tips && state.company.tip_settings?.type == "pre_order";
  let selectedArea = state.areas.selectedArea;
  let outlet = state.outlet;
  let riderTip = state.riderTip;

  return {
    user,
    loyalty,
    orderType,
    loyaltyEnabled,
    cart,
    menu,
    currency,
    discount,
    tipsEnabled,
    selectedArea,
    outlet,
    riderTip,
  };
};

const mapDispatchToProps = {
  fetchLoyalty: loyaltyOperations.fetchLoyalty,
  fetchMyFatoorahPaymentModes: paymentTypeOperations.fetchMyFatoorahPaymentModes,
  setCart: cartOperations.setCart,
  removeRiderTip: riderTipOperations.removeRiderTip,
  buildGenericPaymentsPayload: paymentTypeOperations.buildGenericPaymentsPayload,
  buildGenericStandalonePaymentsPayload: paymentTypeOperations.buildGenericStandalonePaymentsPayload
}

export default connect(mapStateToProps, mapDispatchToProps)(PaymentDetailsMobile);
